import * as React from 'react';
import styled from 'styled-components';
import Select, {
  components as Components,
  Props,
  OptionsType,
} from 'react-select';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

const allOption = {
  name: 'Select all',
  value: '*',
};

const ValueContainer = prps => {
  const currentValues = prps.getValue();
  let toBeRendered = prps.children;
  if (
    currentValues.some(val => val.value === allOption.value)
  ) {
    toBeRendered = [
      [prps.children[0][0]],
      prps.children[1],
    ];
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Components.ValueContainer {...prps}>
      {toBeRendered}
    </Components.ValueContainer>
  );
};

const MultiValue = prps => {
  const isShowCompanyName = prps?.selectProps?.showCompany;
  // Chips normally render `data.name`, which drops any extra context the
  // consumer put in getOptionLabel (e.g. "<type> - <jurisdiction>"). Honouring
  // getOptionLabel unconditionally would rewrite the chips of every select in
  // the app — notably the contact pickers, whose labels are full
  // "name - email - company" strings — so it is opt-in per select.
  const optionLabel = prps?.selectProps?.labelChipsWithOptionLabel
    ? prps.selectProps.getOptionLabel?.(prps.data)
    : null;
  let labelToBeDisplayed =
    optionLabel ||
    `${
      prps.data.name ||
      prps.data.title ||
      (isShowCompanyName &&
        (() => {
          const name = [prps.data.firstName, prps.data.lastName].filter(Boolean).join(' ');
          const parts = [name, prps.data.email, prps.data.companyName].filter(Boolean);
          return parts.join(' - ');
        })())
    }`;

  if (prps.data.value === allOption.value) {
    labelToBeDisplayed = 'All is selected';
  }
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Components.MultiValue {...prps}>
      <span>{labelToBeDisplayed}</span>
    </Components.MultiValue>
  );
};

const customAddPermitStyle = {
  control: styles => styles,
  option: (styles, { data }) => {
    if (data.button) {
      return {
        ...styles,
        color: 'white',
        backgroundColor: '#0b6b9b',
        cursor: 'pointer',
      };
    }
    return styles;
  },
  input: styles => styles,
  placeholder: styles => styles,
  singleValue: styles => styles,
};

const customTableStyles = {
  control: styles => styles,
  option: styles => {
    return {
      ...styles,
      padding: '4px 12px',
      fontSize: '12px',
    };
  },
  menuList: styles => {
    return {
      ...styles,
      padding: 0,
    };
  },
  menu: styles => {
    return {
      ...styles,
      marginTop: '-11px',
    };
  },
  input: styles => styles,
  placeholder: styles => styles,
  singleValue: styles => styles,
};

const SelectInput = (props: SelectTypes) => {
  const {
    components,
    error,
    selectAllActive,
    options,
    isMulti,
    defaultStyles,
    tableStyles,
    dangerBackground,
  } = props;
  const DropdownIndicator = prps => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Components.DropdownIndicator {...prps}>
      {prps.selectProps.menuIsOpen ? (
        <ArrowDropUpIcon style={{ height: "22px" }} />
      ) : (
        <ArrowDropDownIcon style={{ height: "22px" }} />
      )}
    </Components.DropdownIndicator>
  );

  if (selectAllActive && isMulti) {
    return (
      <WrapperForSelectWithOpenMenu error={error}>
        <Select
          components={{
            IndicatorSeparator: null,
            DropdownIndicator,
            MultiValue,
            ValueContainer,
            ...components,
          }}
          classNamePrefix="react-select"
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          options={
            [allOption, ...options] as OptionsType<any>
          }
          onChange={(selected, event) => {
            if (selected !== null && selected.length > 0) {
              if (
                selected[selected.length - 1].value ===
                allOption.value
              ) {
                return props.onChange([
                  allOption,
                  ...options,
                ]);
              }
              if (
                selected.includes(allOption) &&
                (event.action === 'remove-value' ||
                  (event.action === 'deselect-option' &&
                    event.option === allOption))
              ) {
                return props.onChange([]);
              }
              let result = [];
              if (selected.length === options.length) {
                if (selected.includes(allOption)) {
                  result = selected.filter(
                    option =>
                      option.value !== allOption.value,
                  );
                } else if (
                  event.action === 'select-option'
                ) {
                  result = [allOption, ...options];
                }
                return props.onChange(result);
              }
            }

            return props.onChange(selected);
          }}
        />
      </WrapperForSelectWithOpenMenu>
    );
  }

  if (isMulti && !selectAllActive) {
    return (
      <WrapperForSelectWithOpenMenu error={error}>
        <Select
          components={{
            IndicatorSeparator: null,
            DropdownIndicator,
            ...components,
          }}
          classNamePrefix="react-select"
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </WrapperForSelectWithOpenMenu>
    );
  }

  if (defaultStyles || tableStyles) {
    return (
      <Container hasError={error} dangerBackground={dangerBackground}>
        <Select
          components={{
            IndicatorSeparator: null,
            DropdownIndicator,
            ...components,
          }}
          classNamePrefix="react-select"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          styles={
            defaultStyles
              ? customAddPermitStyle
              : customTableStyles
          }
        />
      </Container>
    );
  }
  return (
    <Container hasError={error} dangerBackground={dangerBackground}>
      <Select
        components={{
          IndicatorSeparator: null,
          DropdownIndicator,
          ...components,
        }}
        classNamePrefix="react-select"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </Container>
  );
};

const Container = styled.div<{ hasError: boolean; dangerBackground?: boolean }>`
  width: 100%;

  .react-select {
    &__container {
      outline: none !important;
    }

    &__control {
      border-radius: var(--radius-md);
      box-shadow: none;
      min-height: 40px;
      background: ${({ dangerBackground }) =>
        dangerBackground ? 'var(--danger-50)' : 'var(--neutral-0)'};
      border-color: ${({ hasError, dangerBackground }) =>
        hasError || dangerBackground
          ? 'var(--danger-500)'
          : 'var(--neutral-300)'};
      transition:
        border-color var(--motion-fast) var(--ease-standard),
        box-shadow var(--motion-fast) var(--ease-standard);

      &:hover {
        border-color: ${({ hasError, dangerBackground }) =>
          hasError || dangerBackground
            ? 'var(--danger-600)'
            : 'var(--neutral-400)'};
      }

      &--is-focused {
        border-color: ${({ hasError, dangerBackground }) =>
          hasError || dangerBackground
            ? 'var(--danger-500)'
            : 'var(--primary-600)'};
        box-shadow: ${({ hasError, dangerBackground }) =>
          hasError || dangerBackground
            ? 'var(--shadow-focus-danger)'
            : 'var(--shadow-focus)'};
      }

      &--is-disabled {
        background: ${({ dangerBackground }) =>
          dangerBackground ? 'var(--danger-50)' : 'var(--neutral-100)'};
      }
    }

    &__value-container {
      padding: 2px 10px;
    }

    &__single-value,
    &__input-container {
      color: var(--neutral-800);
      font-size: var(--fs-base);
    }

    &__placeholder {
      color: var(--neutral-400);
      font-size: var(--fs-base);
    }

    &__menu {
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-md);
      border: 1px solid var(--neutral-200);
      overflow: hidden;
      z-index: 20;
    }

    &__option {
      text-align: left;
      padding: 8px 12px;
      font-size: var(--fs-base);
      color: var(--neutral-800);
      cursor: pointer;
      transition: background-color var(--motion-fast)
        var(--ease-standard);

      &--is-focused {
        text-align: left;
        background: var(--primary-50);
        color: var(--primary-800);
      }
      &--is-selected {
        background: var(--primary-600);
        color: var(--neutral-0);
      }
      &--is-disabled {
        color: var(--neutral-400);
        background: var(--neutral-50);
        cursor: not-allowed;
        pointer-events: none;

        &.react-select__option--is-focused {
          background: var(--neutral-50);
          color: var(--neutral-400);
        }
      }
    }

    &__indicator {
      color: var(--neutral-500);
      &:hover {
        color: var(--neutral-700);
      }
    }
  }
`;

const WrapperForSelectWithOpenMenu = styled.div<Wrapper>`
  width: 100%;

  div > .react-select__control {
    border-radius: var(--radius-md);
    min-height: 40px;
    ${({ error }) =>
      error && 'border-color: var(--danger-500);'}
  }

  .react-select__option {
    padding: 8px 12px;
    font-size: var(--fs-base);

    &--is-focused {
      background: var(--primary-50);
      color: var(--primary-800);
    }

    &--is-selected {
      background: var(--primary-600);
      color: var(--neutral-0);

      &::after {
        content: '✓';
        position: absolute;
        right: 2%;
      }
    }

    &--is-disabled {
      color: var(--neutral-400);
      background: var(--neutral-50);
      cursor: not-allowed;
      pointer-events: none;

      &.react-select__option--is-focused {
        background: var(--neutral-50);
        color: var(--neutral-400);
      }
    }
  }
`;

interface Wrapper {
  error?: boolean;
}

export type SelectTypes = Props & {
  selectAllActive?: boolean;
  showCompany?: boolean;
  tableStyles?: boolean;
  /** Render selected chips with getOptionLabel instead of the bare `name`. */
  labelChipsWithOptionLabel?: boolean;
  /** Tints the control red to flag a field that needs the user's attention. */
  dangerBackground?: boolean;
};

export default SelectInput;
